import { useState } from "react";
import { toast } from "react-toastify";
import { Input } from "@/components/ui/input";
import { IconPickerPopover } from "./IconPickerPopover";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Trash } from "@phosphor-icons/react";
import {
  ModulePricing,
  ModuleCategory,
  RealEstateKey,
  CategoryMultipliers,
  DEFAULT_CATEGORY_MULTIPLIERS,
  CATEGORY_LABELS,
  CATEGORY_ORDER,
  REAL_ESTATE_LABELS,
  DEFAULT_RE_PRICING_MODEL,
  RealEstatePricingModelMap,
} from "../types";

interface ModulePricingEditorProps {
  pricing: ModulePricing;
  onChange: (pricing: ModulePricing) => void;
  categoryMultipliers?: CategoryMultipliers;
  rePricingModel?: RealEstatePricingModelMap;
}

export const ModulePricingEditor = ({
  pricing,
  onChange,
  categoryMultipliers = DEFAULT_CATEGORY_MULTIPLIERS,
  rePricingModel = DEFAULT_RE_PRICING_MODEL,
}: ModulePricingEditorProps) => {
  const [showAdd, setShowAdd] = useState(false);
  const [newKey, setNewKey] = useState("");
  const [newLabel, setNewLabel] = useState("");
  const [newCategory, setNewCategory] = useState<ModuleCategory>("vastgoed");
  const [showBreakdown, setShowBreakdown] = useState(false);

  // Calculate totals for all enabled modules
  const reKeys = Object.keys(REAL_ESTATE_LABELS) as RealEstateKey[];
  const enabledModuleKeys = Object.keys(pricing).filter((k) => pricing[k].enabled);
  const totalPerUnit = enabledModuleKeys.reduce((s, k) => s + pricing[k].pricePerUnit, 0);
  const totalPerM2 = enabledModuleKeys.reduce((s, k) => s + pricing[k].pricePerM2, 0);

  const handleToggle = (key: string) => {
    onChange({
      ...pricing,
      [key]: { ...pricing[key], enabled: !pricing[key].enabled },
    });
  };

  const handleFieldChange = (
    key: string,
    field: "pricePerUnit" | "pricePerM2" | "label" | "description",
    value: string | number
  ) => {
    onChange({
      ...pricing,
      [key]: { ...pricing[key], [field]: value },
    });
  };

  const handleDelete = (key: string) => {
    const next = { ...pricing };
    delete next[key];
    onChange(next);
  };

  const handleAdd = () => {
    const safeKey = newKey.trim() || newLabel.trim().toLowerCase().replace(/\s+/g, "_");
    if (!safeKey || pricing[safeKey]) return;
    onChange({
      ...pricing,
      [safeKey]: {
        label: newLabel.trim() || safeKey,
        description: "",
        icon: "\uD83D\uDCE6",
        category: newCategory,
        enabled: true,
        pricePerUnit: 1.0,
        pricePerM2: 0.01,
      },
    });
    toast.info("Module toegevoegd \u2014 vergeet niet op te slaan");
    setNewKey("");
    setNewLabel("");
    setShowAdd(false);
  };

  const allKeys = Object.keys(pricing);

  const grouped = CATEGORY_ORDER.map((cat) => ({
    category: cat,
    label: CATEGORY_LABELS[cat],
    modules: allKeys.filter((k) => pricing[k].category === cat),
  })).filter((g) => g.modules.length > 0);

  return (
    <div className="space-y-4">
      {/* Summary: total price per unit & m² */}
      <div className="rounded-lg border bg-card p-4 flex items-start justify-between gap-4">
        <div className="flex items-center gap-6">
          <div>
            <p className="text-[10px] text-neutral-secondary-mid uppercase tracking-wider">Totaal /unit /mnd</p>
            <p className="text-lg font-bold text-primary tabular-nums">&euro;{totalPerUnit.toFixed(2)}</p>
          </div>
          <div>
            <p className="text-[10px] text-neutral-secondary-mid uppercase tracking-wider">Totaal /m&sup2; /mnd</p>
            <p className="text-lg font-bold text-primary tabular-nums">&euro;{totalPerM2.toFixed(3)}</p>
          </div>
          <p className="text-[10px] text-neutral-secondary-mid">{enabledModuleKeys.length} actieve modules (100% basis)</p>
        </div>
        <button onClick={() => setShowBreakdown(!showBreakdown)}
          className="text-[10px] text-primary hover:underline whitespace-nowrap">
          {showBreakdown ? "Verberg details \u25B2" : "Per categorie \u25BC"}
        </button>
      </div>
      {showBreakdown && (
        <div className="rounded-lg border bg-card overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b">
                <th className="text-left p-2 text-[10px] text-neutral-secondary-mid font-semibold">Categorie</th>
                <th className="text-center p-2 text-[10px] text-neutral-secondary-mid font-semibold">Model</th>
                <th className="text-right p-2 text-[10px] text-neutral-secondary-mid font-semibold">Factor</th>
                <th className="text-right p-2 text-[10px] text-neutral-secondary-mid font-semibold">Prijs /eenheid</th>
              </tr>
            </thead>
            <tbody>
              {reKeys.map((reKey) => {
                const info = REAL_ESTATE_LABELS[reKey];
                const isM2 = rePricingModel[reKey] === "m2";
                const mult = categoryMultipliers[reKey] ?? 1;
                const effectivePrice = isM2 ? totalPerM2 * mult : totalPerUnit * mult;
                return (
                  <tr key={reKey} className="border-b last:border-b-0">
                    <td className="p-2">{info.icon} {info.label}</td>
                    <td className="p-2 text-center">
                      <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-semibold ${isM2 ? "bg-blue-50 text-blue-700" : "bg-emerald-50 text-emerald-700"}`}>
                        {isM2 ? "m\u00B2" : "unit"}
                      </span>
                    </td>
                    <td className="p-2 text-right text-neutral-secondary-mid">{Math.round(mult * 100)}%</td>
                    <td className="p-2 text-right font-medium tabular-nums">
                      &euro;{effectivePrice.toFixed(isM2 ? 3 : 2)} /{isM2 ? "m\u00B2" : "unit"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {grouped.map((group) => (
        <div key={group.category}>
          <p className="text-[10px] font-semibold text-neutral-secondary-mid uppercase tracking-wider mb-2">
            {group.label}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2">
            {group.modules.map((key) => {
              const item = pricing[key];
              return (
                <Card
                  key={key}
                  className={`transition-all ${
                    item.enabled
                      ? "border-primary/30 bg-card"
                      : "border-border bg-muted/30 opacity-60"
                  }`}
                >
                  <CardContent className="p-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-start gap-2 flex-1 min-w-0">
                        <IconPickerPopover value={item.icon} onChange={(v) => onChange({ ...pricing, [key]: { ...item, icon: v } })} size="sm" />
                        <div className="min-w-0">
                          <Input
                            value={item.label}
                            onChange={(e) => handleFieldChange(key, "label", e.target.value)}
                            className="h-6 text-xs font-semibold p-0 border-none shadow-none bg-transparent focus-visible:ring-1"
                          />
                          <Textarea
                            value={item.description}
                            onChange={(e) => handleFieldChange(key, "description", e.target.value)}
                            placeholder="Beschrijving..."
                            rows={2}
                            className="text-xs text-neutral-secondary-mid mt-1 resize-none min-h-0"
                          />
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        <Switch
                          checked={item.enabled}
                          onCheckedChange={() => handleToggle(key)}
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-5 w-5 p-0 text-destructive/50 hover:text-destructive"
                          onClick={() => handleDelete(key)}
                        >
                          <Trash size={10} />
                        </Button>
                      </div>
                    </div>
                    {item.enabled && (
                      <div className="mt-2 pl-7 space-y-1">
                        <div className="flex items-center gap-1.5">
                          <span className="text-[10px] text-neutral-secondary-mid w-3">
                            &euro;
                          </span>
                          <Input
                            type="number"
                            step="0.5"
                            min="0"
                            value={item.pricePerUnit ?? ""}
                            onChange={(e) =>
                              handleFieldChange(key, "pricePerUnit", e.target.value === "" ? 0 : parseFloat(e.target.value))
                            }
                            className="w-24 h-7 text-xs"
                          />
                          <span className="text-[10px] text-neutral-secondary-mid">
                            / unit / mnd
                          </span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <span className="text-[10px] text-neutral-secondary-mid w-3">
                            &euro;
                          </span>
                          <Input
                            type="number"
                            step="0.001"
                            min="0"
                            value={item.pricePerM2 ?? ""}
                            onChange={(e) =>
                              handleFieldChange(key, "pricePerM2", e.target.value === "" ? 0 : parseFloat(e.target.value))
                            }
                            className="w-24 h-7 text-xs"
                          />
                          <span className="text-[10px] text-neutral-secondary-mid">
                            / m&sup2; / mnd
                          </span>
                        </div>

                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      ))}

      {/* Add module */}
      {showAdd ? (
        <Card className="border-dashed">
          <CardContent className="p-3">
            <p className="text-xs font-semibold mb-2">Nieuwe module</p>
            <div className="flex flex-wrap gap-2 items-end">
              <div>
                <label className="text-[10px] text-neutral-secondary-mid block mb-0.5">
                  Naam
                </label>
                <Input
                  value={newLabel}
                  onChange={(e) => setNewLabel(e.target.value)}
                  placeholder="Module naam..."
                  className="h-7 text-xs w-40"
                />
              </div>
              <div>
                <label className="text-[10px] text-neutral-secondary-mid block mb-0.5">
                  Categorie
                </label>
                <Select
                  value={newCategory}
                  onValueChange={(v) => setNewCategory(v as ModuleCategory)}
                >
                  <SelectTrigger className="h-7 text-xs w-44">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent position="popper" sideOffset={4}>
                    {CATEGORY_ORDER.map((cat) => (
                      <SelectItem key={cat} value={cat} className="text-xs">
                        {CATEGORY_LABELS[cat]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                size="sm"
                onClick={handleAdd}
                disabled={!newLabel.trim()}
                className="h-7 text-xs gap-1"
              >
                <Plus size={10} />
                Toevoegen
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowAdd(false)}
                className="h-7 text-xs"
              >
                Annuleren
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowAdd(true)}
          className="gap-1.5 text-xs"
        >
          <Plus size={12} />
          Module toevoegen
        </Button>
      )}
    </div>
  );
};
